// PaywallPage.jsx — FULL FIXED FILE
// Key fix: handlePay() now calls /api/create-order first to get a Razorpay
// order_id. Without this, Razorpay's signature can't be verified server-side,
// causing the "Unexpected end of JSON input" / 404 crash loop.

import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Shield, Clock, Star, Download,
  CheckCircle, Loader, Lock, Zap, Tag, X,
  ChevronRight, Sparkles, AlertCircle,
} from 'lucide-react';
import ATSScoreCard from './ATSScoreCard';
import PaymentSuccess from './PaymentSuccess';
import { useRazorpay } from '../../hooks/useRazorpay';

const PRICE_MRP    = 399;
const PRICE_NORMAL = 99;
const PRICE_COUPON = 59;
const VALID_COUPON = 'TYPEMYCV100';

const injectPaywallStyles = () => {
  if (document.getElementById('pw-styles')) return;
  const s = document.createElement('style');
  s.id = 'pw-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

    .pw-root {
      min-height: 100vh;
      background: var(--rb-cream, #FAF8F5);
      font-family: 'DM Sans', sans-serif;
      color: var(--rb-ink, #1A1814);
      display: flex;
      flex-direction: column;
    }
    .pw-progress-strip {
      height: 3px;
      background: linear-gradient(90deg, var(--rb-gold, #C8973A) 0%, var(--rb-gold-lt, #E8BF72) 60%, transparent 100%);
    }
    .pw-header {
      position: sticky; top: 0; z-index: 20;
      padding: 16px 32px;
      display: flex; align-items: center; justify-content: space-between;
      background: rgba(250,248,245,0.92);
      backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--rb-border, rgba(26,24,20,0.12));
    }
    .pw-logo {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 1.35rem; font-weight: 700; color: var(--rb-ink, #1A1814);
      letter-spacing: -0.5px; display: flex; align-items: baseline; gap: 0;
    }
    .pw-logo-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--rb-gold, #C8973A); display: inline-block;
      margin-left: 2px; position: relative; top: -3px;
    }
    .pw-back-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 15px; border-radius: 50px;
      border: 1px solid var(--rb-border-md, rgba(26,24,20,0.18));
      background: transparent;
      font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 600;
      color: var(--rb-ink, #1A1814); cursor: pointer; transition: all 0.16s;
    }
    .pw-back-btn:hover { background: rgba(26,24,20,0.05); }
    .pw-steps { display: flex; align-items: center; gap: 0; }
    .pw-step-item {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.73rem; font-weight: 600;
      color: var(--rb-muted, rgba(26,24,20,0.45)); white-space: nowrap;
    }
    .pw-step-item.active { color: var(--rb-ink, #1A1814); }
    .pw-step-item.done   { color: var(--rb-gold, #C8973A); }
    .pw-step-dot {
      width: 22px; height: 22px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.65rem; font-weight: 800;
      border: 1.5px solid var(--rb-border-md, rgba(26,24,20,0.18));
      background: transparent; color: var(--rb-muted, rgba(26,24,20,0.45));
      transition: all 0.2s;
    }
    .pw-step-item.active .pw-step-dot {
      border-color: var(--rb-ink, #1A1814);
      background: var(--rb-ink, #1A1814);
      color: #FAF8F5;
    }
    .pw-step-item.done .pw-step-dot {
      border-color: var(--rb-gold, #C8973A);
      background: var(--rb-gold, #C8973A);
      color: #fff;
    }
    .pw-step-line {
      width: 28px; height: 1px;
      background: var(--rb-border-md, rgba(26,24,20,0.18));
      margin: 0 4px;
    }
    .pw-body {
      flex: 1; max-width: 780px; width: 100%;
      margin: 0 auto; padding: 36px 24px 80px;
    }
    .pw-section-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.5rem; font-weight: 700;
      color: var(--rb-ink, #1A1814);
      margin: 0 0 6px;
    }
    .pw-section-sub {
      font-size: 0.84rem;
      color: var(--rb-muted, rgba(26,24,20,0.45));
      margin: 0 0 28px; line-height: 1.5;
    }
    .pw-price-card {
      background: var(--rb-card-bg, #FFFFFF);
      border: 1.5px solid var(--rb-border-md, rgba(26,24,20,0.18));
      border-radius: 18px;
      box-shadow: 0 4px 32px rgba(26,24,20,0.08);
      overflow: hidden;
      transition: box-shadow 0.2s;
    }
    .pw-price-card:hover { box-shadow: 0 16px 48px rgba(26,24,20,0.13); }
    .pw-price-card.featured { border-color: var(--rb-gold, #C8973A); }
    .pw-price-top-bar {
      height: 3px;
      background: linear-gradient(90deg, var(--rb-gold, #C8973A), var(--rb-gold-lt, #E8BF72));
    }
    .pw-price-header {
      padding: 24px 28px 20px;
      border-bottom: 1px solid var(--rb-border, rgba(26,24,20,0.08));
      display: flex; align-items: flex-start; justify-content: space-between;
      gap: 16px;
    }
    .pw-price-name {
      font-family: 'Playfair Display', serif;
      font-size: 1.15rem; font-weight: 700;
      margin-bottom: 14px;
    }
    .pw-price-stack { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
    .pw-price-mrp {
      font-size: 1.1rem; color: var(--rb-muted, rgba(26,24,20,0.4));
      text-decoration: line-through; font-weight: 500;
    }
    .pw-price-current { display: flex; align-items: baseline; gap: 2px; }
    .pw-price-currency { font-size: 1.1rem; font-weight: 700; color: var(--rb-ink, #1A1814); }
    .pw-price-value {
      font-size: 2.6rem; font-weight: 800; color: var(--rb-ink, #1A1814);
      line-height: 1; transition: all 0.3s ease;
    }
    .pw-price-value.discounted { color: #1A7A4A; }
    .pw-price-once { font-size: 0.75rem; color: var(--rb-muted, rgba(26,24,20,0.4)); margin-left: 4px; }
    .pw-price-savings {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 10px; border-radius: 50px;
      font-size: 0.7rem; font-weight: 700;
      background: rgba(26,122,74,0.10); color: #1A7A4A;
      border: 1px solid rgba(26,122,74,0.20); margin-top: 6px;
    }
    .pw-price-badge {
      padding: 4px 12px; border-radius: 50px;
      font-size: 0.68rem; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.07em;
      background: rgba(200,151,58,0.10); color: #9A6F1E;
      border: 1px solid rgba(200,151,58,0.25); white-space: nowrap;
    }
    .pw-price-body { padding: 22px 28px 26px; }
    .pw-feature {
      display: flex; align-items: center; gap: 10px;
      font-size: 0.83rem; color: var(--rb-ink, #1A1814); margin-bottom: 12px;
    }
    .pw-feature-icon { color: var(--rb-gold, #C8973A); flex-shrink: 0; }
    .pw-ats-banner {
      padding: 11px 15px; border-radius: 10px; margin-bottom: 20px;
      background: rgba(200,151,58,0.08); border: 1px solid rgba(200,151,58,0.22);
      font-size: 0.8rem; color: #9A6F1E; font-weight: 600;
      display: flex; align-items: center; gap: 8px;
    }
    .pw-coupon-wrapper { margin-bottom: 20px; }
    .pw-coupon-hint {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 5px 12px; border-radius: 6px; margin-bottom: 10px;
      font-family: 'DM Mono', monospace;
      font-size: 0.72rem; font-weight: 500; letter-spacing: 0.06em;
      background: rgba(26,122,74,0.07); color: #1A7A4A;
      border: 1px dashed rgba(26,122,74,0.28);
      cursor: pointer; transition: background 0.18s;
    }
    .pw-coupon-hint:hover { background: rgba(26,122,74,0.13); }
    .pw-coupon-hint-label {
      font-family: 'DM Sans', sans-serif; font-size: 0.72rem;
      color: rgba(26,122,74,0.7); font-weight: 400;
    }
    .pw-coupon-row { display: flex; gap: 8px; align-items: stretch; }
    .pw-coupon-input {
      flex: 1; padding: 10px 14px; border-radius: 10px;
      border: 1.5px solid var(--rb-border-md, rgba(26,24,20,0.18));
      font-family: 'DM Mono', monospace; font-size: 0.85rem; font-weight: 500;
      letter-spacing: 0.06em; color: var(--rb-ink, #1A1814);
      background: var(--rb-cream, #FAF8F5); outline: none;
      transition: border-color 0.18s; text-transform: uppercase;
    }
    .pw-coupon-input:focus { border-color: var(--rb-gold, #C8973A); }
    .pw-coupon-input.valid   { border-color: #1A7A4A; background: rgba(26,122,74,0.04); }
    .pw-coupon-input.invalid { border-color: #B83232; background: rgba(184,50,50,0.04); }
    .pw-coupon-apply-btn {
      padding: 10px 18px; border-radius: 10px;
      border: 1.5px solid var(--rb-ink, #1A1814);
      background: var(--rb-ink, #1A1814); color: #FAF8F5;
      font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 600;
      cursor: pointer; transition: all 0.18s; white-space: nowrap;
    }
    .pw-coupon-apply-btn:hover {
      background: var(--rb-gold, #C8973A); border-color: var(--rb-gold, #C8973A);
      color: var(--rb-ink, #1A1814);
    }
    .pw-coupon-apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .pw-coupon-status {
      margin-top: 7px; font-size: 0.78rem; font-weight: 600;
      display: flex; align-items: center; gap: 5px;
    }
    .pw-coupon-status.valid   { color: #1A7A4A; }
    .pw-coupon-status.invalid { color: #B83232; }
    .pw-coupon-remove {
      background: none; border: none; cursor: pointer;
      color: rgba(26,24,20,0.4); font-size: 0.75rem;
      display: inline-flex; align-items: center; gap: 4px;
      padding: 0; transition: color 0.15s; margin-top: 4px;
    }
    .pw-coupon-remove:hover { color: #B83232; }
    .pw-pay-btn {
      width: 100%; display: flex; align-items: center;
      justify-content: center; gap: 8px;
      padding: 15px 20px; border-radius: 50px; border: none; cursor: pointer;
      background: var(--rb-ink, #1A1814); color: #FAF8F5;
      font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 700;
      transition: all 0.18s; margin-top: 20px; letter-spacing: 0.01em;
    }
    .pw-pay-btn:hover:not(:disabled) {
      background: var(--rb-gold, #C8973A); color: var(--rb-ink, #1A1814);
      transform: translateY(-1px); box-shadow: 0 6px 20px rgba(200,151,58,0.32);
    }
    .pw-pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .pw-trust {
      display: flex; align-items: center; justify-content: center;
      gap: 18px; margin-top: 16px; flex-wrap: wrap;
    }
    .pw-trust-item {
      display: flex; align-items: center; gap: 5px;
      font-size: 0.72rem; color: var(--rb-muted, rgba(26,24,20,0.45)); font-weight: 500;
    }
    .pw-order-summary {
      margin-top: 18px; padding: 14px 16px; border-radius: 10px;
      background: var(--rb-cream, #FAF8F5);
      border: 1px solid var(--rb-border, rgba(26,24,20,0.08)); font-size: 0.8rem;
    }
    .pw-order-row {
      display: flex; justify-content: space-between;
      align-items: center; padding: 3px 0;
      color: var(--rb-muted, rgba(26,24,20,0.5));
    }
    .pw-order-row.total {
      border-top: 1px solid var(--rb-border, rgba(26,24,20,0.10));
      margin-top: 6px; padding-top: 9px;
      font-weight: 700; font-size: 0.9rem; color: var(--rb-ink, #1A1814);
    }
    .pw-order-row.discount { color: #1A7A4A; font-weight: 600; }
    @media (max-width: 640px) {
      .pw-header { padding: 13px 16px; }
      .pw-steps { display: none; }
      .pw-body { padding: 24px 16px 60px; }
      .pw-section-title { font-size: 1.2rem; }
      .pw-price-header { flex-direction: column; }
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pw-slide-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .pw-animate-in { animation: pw-slide-in 0.28s ease both; }
  `;
  document.head.appendChild(s);
};

const Steps = ({ current }) => {
  const steps = ['ATS Check', 'Payment', 'Download'];
  return (
    <div className="pw-steps">
      {steps.map((label, i) => {
        const state = i + 1 < current ? 'done' : i + 1 === current ? 'active' : '';
        return (
          <React.Fragment key={i}>
            <div className={`pw-step-item ${state}`}>
              <div className="pw-step-dot">
                {state === 'done' ? <CheckCircle size={12} /> : i + 1}
              </div>
              {label}
            </div>
            {i < steps.length - 1 && <div className="pw-step-line" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PricingCard = ({ atsScore, onPay, paying }) => {
  const [couponInput,   setCouponInput]   = useState('');
  const [couponState,   setCouponState]   = useState('idle');
  const [finalPrice,    setFinalPrice]    = useState(PRICE_NORMAL);
  const [hintDismissed, setHintDismissed] = useState(false);
  const inputRef = useRef(null);

  const savings      = PRICE_MRP - finalPrice;
  const couponActive = couponState === 'valid';

  const fillHint = () => {
    setCouponInput(VALID_COUPON);
    setHintDismissed(true);
    inputRef.current?.focus();
  };

  const handleApply = () => {
    const code = couponInput.trim().toUpperCase();
    if (code === VALID_COUPON) { setCouponState('valid');   setFinalPrice(PRICE_COUPON);  }
    else                       { setCouponState('invalid'); setFinalPrice(PRICE_NORMAL); }
  };

  const handleRemoveCoupon = () => {
    setCouponInput(''); setCouponState('idle');
    setFinalPrice(PRICE_NORMAL); setHintDismissed(false);
  };

  return (
    <div className="pw-price-card featured">
      <div className="pw-price-top-bar" />
      <div className="pw-price-header">
        <div>
          <div className="pw-price-name">Resume Download</div>
          <div className="pw-price-stack">
            <span className="pw-price-mrp">₹{PRICE_MRP}</span>
            <div className="pw-price-current">
              <span className="pw-price-currency">₹</span>
              <span className={`pw-price-value ${couponActive ? 'discounted' : ''}`}>{finalPrice}</span>
              <span className="pw-price-once">one-time</span>
            </div>
          </div>
          <div className="pw-price-savings pw-animate-in" key={finalPrice}>
            <Sparkles size={10} />
            You save ₹{savings} ({Math.round((savings / PRICE_MRP) * 100)}% off)
          </div>
        </div>
        <span className="pw-price-badge">Best Value</span>
      </div>

      <div className="pw-price-body">
        {atsScore !== null && (
          <div className="pw-ats-banner">
            <Star size={13} />
            Your ATS score: <strong>{atsScore}/100</strong> — saved &amp; included!
          </div>
        )}

        {[
          'ATS-optimised LaTeX PDF resume',
          'Instant download after payment',
          'Free ATS score check included',
          'Unlimited edits before downloading',
          'Recruiter-ready formatting',
        ].map((f, i) => (
          <div className="pw-feature" key={i}>
            <CheckCircle size={15} className="pw-feature-icon" /> {f}
          </div>
        ))}

        <div className="pw-coupon-wrapper">
          {!couponActive && !hintDismissed && (
            <div style={{ marginBottom: 8 }}>
              <button className="pw-coupon-hint" onClick={fillHint} type="button">
                <Tag size={12} />
                <span style={{ letterSpacing: '0.1em' }}>{VALID_COUPON}</span>
                <span className="pw-coupon-hint-label">— tap to apply &amp; save ₹{PRICE_NORMAL - PRICE_COUPON}</span>
              </button>
            </div>
          )}
          {!couponActive ? (
            <>
              <div className="pw-coupon-row">
                <input
                  ref={inputRef}
                  className={`pw-coupon-input ${couponState === 'invalid' ? 'invalid' : ''}`}
                  type="text" placeholder="Enter coupon code"
                  value={couponInput}
                  onChange={e => { setCouponInput(e.target.value); if (couponState !== 'idle') setCouponState('idle'); }}
                  onKeyDown={e => e.key === 'Enter' && handleApply()}
                  maxLength={20}
                />
                <button className="pw-coupon-apply-btn" onClick={handleApply}
                  disabled={!couponInput.trim()} type="button">Apply</button>
              </div>
              {couponState === 'invalid' && (
                <div className="pw-coupon-status invalid pw-animate-in">
                  <X size={12} /> Invalid code. Try <strong>{VALID_COUPON}</strong>
                </div>
              )}
            </>
          ) : (
            <div className="pw-animate-in">
              <div className="pw-coupon-status valid">
                <CheckCircle size={13} />
                <strong>{VALID_COUPON}</strong> applied — saving ₹{PRICE_NORMAL - PRICE_COUPON}!
              </div>
              <button className="pw-coupon-remove" onClick={handleRemoveCoupon} type="button">
                <X size={11} /> Remove coupon
              </button>
            </div>
          )}
        </div>

        <div className="pw-order-summary">
          <div className="pw-order-row"><span>Resume PDF download</span><span>₹{PRICE_MRP}</span></div>
          <div className="pw-order-row discount"><span>Discount</span><span>−₹{savings}</span></div>
          {couponActive && (
            <div className="pw-order-row discount">
              <span>Coupon ({VALID_COUPON})</span><span>−₹{PRICE_NORMAL - PRICE_COUPON}</span>
            </div>
          )}
          <div className="pw-order-row total"><span>Total</span><span>₹{finalPrice}</span></div>
        </div>

        <button className="pw-pay-btn" onClick={() => onPay(finalPrice)} disabled={paying} type="button">
          {paying
            ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Processing…</>
            : <><Lock size={15} /> Pay ₹{finalPrice} &amp; Download</>}
        </button>

        <div className="pw-trust">
          <span className="pw-trust-item"><Shield size={12} /> Razorpay Secured</span>
          <span className="pw-trust-item"><Zap size={12} /> Instant PDF</span>
          <span className="pw-trust-item"><Clock size={12} /> 5-min checkout</span>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const PaywallPage = ({ getLatexSource, resumeText = '', onBack }) => {
  useEffect(() => { injectPaywallStyles(); }, []);

  const [step,          setStep]          = useState('ats');
  const [atsScore,      setAtsScore]      = useState(null);
  const [paying,        setPaying]        = useState(false);
  const [paymentId,     setPaymentId]     = useState(null);
  const [downloadToken, setDownloadToken] = useState(null);
  const [latexSource,   setLatexSource]   = useState(null);
  const [verifyError,   setVerifyError]   = useState(null);

  const { isLoaded, openCheckout } = useRazorpay();

  const handleATSDone = (scoreData) => { setAtsScore(scoreData?.overall ?? null); setStep('payment'); };
  const handleSkipATS = ()          => setStep('payment');

  // ── KEY FIX: Create Razorpay order FIRST, then open checkout ──
  // Previously this was missing — Razorpay needs a server-issued order_id
  // to produce a verifiable signature. Without it, verify-payment always
  // fails and returns a non-JSON 404, causing "Unexpected end of JSON input".
  const handlePay = async (finalPrice) => {
    if (!isLoaded) {
      alert('Payment SDK is still loading. Please wait and try again.');
      return;
    }
    setPaying(true);
    setVerifyError(null);

    // ── Step 1: Create order server-side ──────────────────────
    let orderId;
    try {
      const orderRes = await fetch('/api/create-order', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ amount: finalPrice * 100 }), // paise
      });

      // Safe JSON parse — guard against HTML error pages
      const contentType = orderRes.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        const text = await orderRes.text();
        console.error('[PaywallPage] create-order non-JSON response:', text.slice(0, 200));
        throw new Error('Order creation failed (server error). Please try again.');
      }

      const orderData = await orderRes.json();

      if (!orderRes.ok || !orderData.orderId) {
        throw new Error(orderData.error ?? 'Could not create order');
      }

      orderId = orderData.orderId;
    } catch (err) {
      setPaying(false);
      setVerifyError(err.message || 'Could not start payment. Please try again.');
      return;
    }

    // ── Step 2: Open Razorpay checkout with the order ─────────
    openCheckout({
      amount     : finalPrice * 100,
      orderId,                          // ← required for signature verification
      name       : 'TypeMyCv',
      description: 'Resume PDF Download',
      theme      : { color: '#C8973A' },
      prefill    : {},

      onSuccess: async (razorpayResponse) => {
        try {
          // ── Step 3: Verify payment signature server-side ───
          const verifyRes = await fetch('/api/verify-payment', {
            method : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body   : JSON.stringify({
              razorpay_order_id  : razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature : razorpayResponse.razorpay_signature,
            }),
          });

          // Safe JSON parse — guard against HTML error pages (the old crash)
          const contentType = verifyRes.headers.get('content-type') ?? '';
          if (!contentType.includes('application/json')) {
            const text = await verifyRes.text();
            console.error('[PaywallPage] verify-payment non-JSON response:', text.slice(0, 200));
            throw new Error(
              'Payment verification service error. Your payment was received. ' +
              'Please contact support with payment ID: ' + razorpayResponse.razorpay_payment_id
            );
          }

          const data = await verifyRes.json();

          if (!verifyRes.ok || !data.verified || !data.downloadToken) {
            throw new Error(data.error || 'Payment verification failed');
          }

          // ── Step 4: Capture latex BEFORE setting state ─────
          const capturedLatex = getLatexSource();

          // ── Step 5: Set all success state atomically ────────
          setPaymentId(razorpayResponse.razorpay_payment_id);
          setDownloadToken(data.downloadToken);
          setLatexSource(capturedLatex);
          setPaying(false);
          setStep('success'); // step change LAST — all state ready

        } catch (err) {
          setPaying(false);
          setVerifyError(
            err.message ||
            'Payment was received but verification failed. Please contact support with your payment ID: ' +
            razorpayResponse.razorpay_payment_id
          );
        }
      },

      onFailure: (err) => {
        setPaying(false);
        alert(err?.description || 'Payment was not completed. Please try again.');
      },
    });
  };

  // Guard: show PaymentSuccess only when ALL required data is present
  if (step === 'success' && downloadToken && latexSource) {
    return (
      <PaymentSuccess
        downloadToken={downloadToken}
        latexSource={latexSource}
        paymentId={paymentId}
        atsScore={atsScore}
        onReset={onBack}
      />
    );
  }

  // Race-condition safety net: step flipped to success but data not ready yet
  if (step === 'success') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--rb-cream, #FAF8F5)',
        flexDirection: 'column', gap: 16,
      }}>
        <div style={{
          width: 40, height: 40, border: '3px solid rgba(26,24,20,0.1)',
          borderTopColor: 'var(--rb-gold, #C8973A)', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ fontFamily: "'DM Sans', sans-serif", color: 'rgba(26,24,20,0.5)', fontSize: '0.88rem' }}>
          Preparing your download…
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="pw-root">
      <div className="pw-progress-strip" />

      <div className="pw-header">
        <div className="pw-logo">TypeMyCv<span className="pw-logo-dot" /></div>
        <Steps current={step === 'ats' ? 1 : 2} />
        <button className="pw-back-btn" onClick={onBack}>
          <ArrowLeft size={13} /> Back to Editor
        </button>
      </div>

      <div className="pw-body">
        {step === 'ats' && (
          <>
            <h1 className="pw-section-title">Check your ATS score — free</h1>
            <p className="pw-section-sub">
              Paste the job description to see how well your resume matches. Or skip straight to download.
            </p>
            <ATSScoreCard resumeText={resumeText} onSkip={handleSkipATS} onContinue={handleATSDone} />
          </>
        )}

        {step === 'payment' && (
          <>
            <h1 className="pw-section-title">Download your resume</h1>
            <p className="pw-section-sub">
              One-time payment · No subscription · No hidden fees.
              {atsScore !== null && (
                <> Your ATS score of <strong style={{ color: 'var(--rb-ink)' }}>{atsScore}/100</strong> is locked in.</>
              )}
            </p>

            {verifyError && (
              <div style={{
                padding: '12px 16px', borderRadius: 10, marginBottom: 16,
                background: 'rgba(192,57,43,0.07)', border: '1px solid rgba(192,57,43,0.25)',
                fontSize: '0.82rem', color: '#C0392B',
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{verifyError}</span>
              </div>
            )}

            <PricingCard atsScore={atsScore} onPay={handlePay} paying={paying || !isLoaded} />
          </>
        )}
      </div>
    </div>
  );
};

export default PaywallPage;